/*
 * <custom-select> — a styled dropdown that replaces the browser's own.
 *
 * It wraps an existing native <select> rather than replacing it. The select
 * stays in the DOM as the single source of truth, so anything already listening
 * for its "change" event (collection sorting, the hero search) keeps working
 * untouched. The native element is hidden from sight and from screen readers,
 * and this element renders an accessible listbox on top of it.
 *
 * Usage:
 *   <custom-select class="custom-select">
 *     <select name="sort_by">...</select>
 *   </custom-select>
 *
 * Options that appear later (the hero search fetches bike models over the
 * network) are picked up by a MutationObserver, as is the select being
 * enabled or disabled.
 */
(function () {
  if (customElements.get('custom-select')) return;

  var uid = 0;

  class CustomSelect extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.select = this.querySelector('select');
      if (!this.select) return;
      this.initialised = true;

      uid += 1;
      this.idBase = 'custom-select-' + uid;
      this.open = false;

      this.hideNativeSelect();
      this.buildUI();
      this.bindEvents();
      this.sync();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick, true);
      if (this.observer) this.observer.disconnect();
    }

    /* The select keeps working, it just stops being seen or announced. */
    hideNativeSelect() {
      this.select.classList.add('custom-select--native');
      this.select.setAttribute('tabindex', '-1');
      this.select.setAttribute('aria-hidden', 'true');
    }

    buildUI() {
      this.trigger = document.createElement('button');
      this.trigger.type = 'button';
      this.trigger.className = 'custom-select--trigger';
      this.trigger.id = this.idBase + '-trigger';
      this.trigger.setAttribute('aria-haspopup', 'listbox');
      this.trigger.setAttribute('aria-expanded', 'false');

      var labelledBy = this.getAttribute('data-labelled-by');
      if (labelledBy) this.trigger.setAttribute('aria-labelledby', labelledBy + ' ' + this.trigger.id);

      this.triggerText = document.createElement('span');
      this.triggerText.className = 'custom-select--trigger-text';
      this.trigger.appendChild(this.triggerText);

      var arrow = document.createElement('span');
      arrow.className = 'custom-select--arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.innerHTML = '<svg viewBox="0 0 12 8" width="12" height="8" focusable="false"><path d="M1 1.5 6 6.5 11 1.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      this.trigger.appendChild(arrow);

      this.list = document.createElement('ul');
      this.list.className = 'custom-select--list';
      this.list.id = this.idBase + '-list';
      this.list.setAttribute('role', 'listbox');
      this.list.hidden = true;

      this.trigger.setAttribute('aria-controls', this.list.id);

      this.appendChild(this.trigger);
      this.appendChild(this.list);
    }

    bindEvents() {
      this.trigger.addEventListener('click', this.onTriggerClick.bind(this));
      this.trigger.addEventListener('keydown', this.onTriggerKeydown.bind(this));
      this.list.addEventListener('click', this.onListClick.bind(this));
      this.list.addEventListener('keydown', this.onListKeydown.bind(this));

      /* Anything that changes the select behind our back re-renders the UI. */
      this.select.addEventListener('change', this.sync.bind(this));

      this.observer = new MutationObserver(this.sync.bind(this));
      this.observer.observe(this.select, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled']
      });

      this.onDocumentClick = this.onDocumentClick.bind(this);
      document.addEventListener('click', this.onDocumentClick, true);
    }

    /* Rebuild the visible list from whatever the select currently holds. */
    sync() {
      var options = Array.prototype.slice.call(this.select.options);
      var selected = this.select.options[this.select.selectedIndex];

      this.triggerText.textContent = selected ? selected.textContent : '';
      this.trigger.disabled = this.select.disabled;
      this.classList.toggle('custom-select--disabled', this.select.disabled);
      if (this.select.disabled && this.open) this.close();

      this.list.innerHTML = '';
      options.forEach(function (option, index) {
        var item = document.createElement('li');
        item.className = 'custom-select--option';
        item.id = this.idBase + '-option-' + index;
        item.setAttribute('role', 'option');
        item.setAttribute('tabindex', '-1');
        item.dataset.value = option.value;
        item.textContent = option.textContent;

        var isSelected = option === selected;
        item.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        item.classList.toggle('is-selected', isSelected);

        if (option.disabled) {
          item.setAttribute('aria-disabled', 'true');
          item.classList.add('is-disabled');
        }

        this.list.appendChild(item);
      }, this);
    }

    get items() {
      return Array.prototype.slice.call(
        this.list.querySelectorAll('.custom-select--option:not(.is-disabled)')
      );
    }

    onTriggerClick(event) {
      event.preventDefault();
      if (this.open) this.close();
      else this.openList();
    }

    onTriggerKeydown(event) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.openList();
      }
    }

    onListClick(event) {
      var item = event.target.closest('.custom-select--option');
      if (!item || item.classList.contains('is-disabled')) return;
      this.commit(item.dataset.value);
    }

    onListKeydown(event) {
      var items = this.items;
      var current = items.indexOf(document.activeElement);

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.focusItem(items[Math.min(current + 1, items.length - 1)]);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.focusItem(items[Math.max(current - 1, 0)]);
          break;
        case 'Home':
          event.preventDefault();
          this.focusItem(items[0]);
          break;
        case 'End':
          event.preventDefault();
          this.focusItem(items[items.length - 1]);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          if (document.activeElement.dataset) this.commit(document.activeElement.dataset.value);
          break;
        case 'Escape':
          event.preventDefault();
          this.close(true);
          break;
        case 'Tab':
          this.close();
          break;
        default:
          break;
      }
    }

    onDocumentClick(event) {
      if (!this.open) return;
      if (this.contains(event.target)) return;
      this.close();
    }

    focusItem(item) {
      if (item) item.focus();
    }

    openList() {
      if (this.select.disabled || this.open) return;
      this.open = true;
      this.list.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');

      var selected = this.list.querySelector('.is-selected:not(.is-disabled)');
      this.focusItem(selected || this.items[0]);
    }

    close(returnFocus) {
      if (!this.open) return;
      this.open = false;
      this.list.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      if (returnFocus) this.trigger.focus();
    }

    /* Write through to the native select so existing listeners fire as normal. */
    commit(value) {
      var changed = this.select.value !== value;
      this.select.value = value;
      this.sync();
      this.close(true);
      if (changed) {
        this.select.dispatchEvent(new Event('input', { bubbles: true }));
        this.select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  customElements.define('custom-select', CustomSelect);
})();
